import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  forwardRef,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ThisPlatform } from 'src/app/_services/platform.service';
@Component({
  selector: 'app-select-dropdown',
  templateUrl: './select-dropdown.component.html',
  styleUrls: ['./select-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectDropdownComponent),
      multi: true,
    },
  ],
})
export class SelectDropdownComponent implements OnInit, ControlValueAccessor {
  /* przykład użycia:
  
            <div class="control-display">
              <ion-label>{{'COMMUNITY.BUILDING' | translate}}</ion-label>
              <app-select-dropdown [isDisabled]="buildingIsDisabled" formControlName="building"
                [data]="buildingServ.buildings" (changeValueEvent)="buildingChange($event)"></app-select-dropdown>
            </div>

  */
  constructor(
    private translateService: TranslateService,
    private platform: Platform
  ) {}

  isKeyboardVisible: boolean = false;

  @Input()
  isDisabled = false;

  @Input()
  buttonDropdown = false;

  @Input()
  data: any;

  @Input()
  value: any;

  @Input()
  displayKey: string = 'name';

  @Input()
  multiple: boolean = false;

  @Input()
  mobileRoundedCorners: boolean = false;

  @Output()
  changeValueEvent: EventEmitter<any> = new EventEmitter<any>();
  /**
   * Event potrzebny gdy komponent jest wymagany.
   * Żeby prawidłowo go obsłuzyć należy ustawić jego FormControll jako touched np.
   * this.form.get(formControlName).markAsTouched();
   */
  @Output()
  touchEvent: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
    if (this._placeholder) {
      this.config = {
        ...this.config,
        placeholder: this.translateService.instant(this._placeholder),
      };
    }
  }

  _placeholder: string;

  dropdownContainer;

  selectedValue: any;

  optionsList: any = null;

  keyboardDidHideSubscription: Subscription;
  keyboardDidShowSubscription: Subscription;

  config = {
    displayKey: this.displayKey, //if objects array passed which key to be displayed defaults to description
    search: true, //true/false for the search functionlity defaults to false,
    height: 'auto', //height of the list so that if there are more no of items it can show a scroll defaults to auto. With auto height scroll will never appear
    placeholder: this.translateService.instant('SELECT_FROM_LIST'), // text to be displayed when no item is selected defaults to Select,
    customComparator: (i1, i2) => {
      if (isNaN(i1[this.displayKey]) && isNaN(i2[this.displayKey])) {
        let ret =
          i1[this.displayKey].toLowerCase() < i2[this.displayKey].toLowerCase();
        return ret ? -1 : 1;
      } else {
        let ret = +i1[this.displayKey] < +i2[this.displayKey];
        return ret ? -1 : 1;
      }
    },

    // customComparator: (a, b) => {
    //   if (a.name < b.name) {
    //     return -1;
    //   }
    //   if (a.name > b.name) {
    //     return 1;
    //   }
    //   return 0;
    // }, // a custom function using which user wants to sort the items. default is undefined and Array.sort() will be used in that case,
    limitTo: 0, // number thats limits the no of options displayed in the UI (if zero, options will not be limited)
    moreText: this.translateService.instant('MORE'), // text to be displayed whenmore than one items are selected like Option 1 + 5 more
    noResultsFound: this.translateService.instant('NO_RESULTS_FOUND'), // text to be displayed when no items are found while searching
    searchPlaceholder: this.translateService.instant('SEARCH_PLACEHOLDER'), // label thats displayed in search input,
    searchOnKey: 'name', // key on which search should be performed this will be selective search. if undefined this will be extensive search on all keys
    clearOnSelection: true, // clears search criteria when an option is selected if set to true, default is false
    inputDirection: 'ltr', // the direction of the search input can be rtl or ltr(default)
  };

  async ngOnInit() {
    // nadpisanie configa:
    if (this.displayKey) {
      this.config = {
        ...this.config,
        displayKey: this.displayKey,
        searchOnKey: this.displayKey,
      };
      await this.setOptionList();
    }

    this.keyboardDidHideSubscription = this.platform.keyboardDidHide.subscribe(
      () => {
        this.isKeyboardVisible = false;
      }
    );

    this.keyboardDidShowSubscription = this.platform.keyboardDidShow.subscribe(
      () => {
        this.isKeyboardVisible = true;
      }
    );
  }

  onChangeValue($event) {
    if (this.selectedValue === $event.value) {
      this.changeValueEvent.emit(null);
      this.selectedValue = null;
      this.onChange(null);
      this.value = null;
      if (this.multiple != true) {
        this.dropdownContainer.style.visibility = 'hidden';
      }
    } else {
      this.changeValueEvent.emit($event.value);
      this.selectedValue = $event.value;
      this.onChange($event.value);
    }

    if (this.buttonDropdown) {
      this.selectedValue = null;
      this.value = null;
    }

    this.onTouched();
  }

  onOpen() {
    if (this.buttonDropdown) {
      this.value = null;
    }
    this.touchEvent.emit();
  }

  async onSetDropdownHeightAndFilterDuplicates($event) {
    this.setOptionList();

    if (this.isKeyboardVisible) {
      setTimeout(() => {
        this.setDropdownHeight($event);
      }, 500);
    } else {
      this.setDropdownHeight($event);
    }
  }

  _value = false;
  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn) {
    this.onChange = fn;
  }

  setDropdownHeight(event) {
    const bottomPosition =
      event.currentTarget.getBoundingClientRect().top +
      event.currentTarget.getBoundingClientRect().height;
    const viewportHeight = window.innerHeight;
    const dropdownHeight = viewportHeight - bottomPosition;
    this.dropdownContainer =
      event.currentTarget.firstChild.firstChild.nextSibling;

    event.currentTarget.style.height = viewportHeight - bottomPosition;

    if (this.dropdownContainer.style) {
      this.dropdownContainer.style.maxHeight =
        (dropdownHeight > 120 ? dropdownHeight : 120) + 'px';
    }
  }

  writeValue(value) {
    // FIXME:
    // BRAK WARUNKU potrzebny jest w komponencie "parking-assignment-edit.component". Po zmianie firmy resetuje się wcześniej ustawiony użytkownik
    // if (value) {
    this.value = value;
    // }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  switch() {
    this.value = !this.value;
  }

  /** Set option list for dropdown */
  private async setOptionList() {
    // needed to correct setting list of options on iOS
    if(!this.value && !this.optionsList) {
      this.optionsList = this.data;
    } else {
      this.optionsList = this.data.filter((option) => {
        return option[this.displayKey] !== this.value[this.displayKey];
      });
    }
  }

  ngOnDestroy() {
    this.keyboardDidHideSubscription.unsubscribe();
    this.keyboardDidShowSubscription.unsubscribe();
  }
}
